import type { UnknownAction } from '@reduxjs/toolkit';
import { actionOnMessage } from '@skynet/data-ws-mm';
import type { CoinGeckoTerms } from '@skynet/data-helpers';
import { isCoinGeckoLinkAwaitingTerms } from '@skynet/data-helpers';
import { parseResponse } from '@skynet/data-ws-core';

import type { RootState } from 'src/store';
import { startAppListening } from 'src/store/middleware/listenerMiddleware';

/** The part of a loan or retainer terms slice the refresh reads. */
interface CoinGeckoTermsState {
  id?: number;
  details?: CoinGeckoTerms;
  confirmedTerms?: { details?: CoinGeckoTerms };
}

/** What differs between the loan and the retainer terms refresh. */
export interface CoinGeckoLinkRefreshConfig {
  /** The WS command whose ack settles the terms update, e.g. `UpdateLoan`. */
  updateCommand: string;
  /** The terms slice the update was applied to. */
  selectTerms: (state: RootState) => CoinGeckoTermsState;
  /** Whether the terms page of the entity with this id is the one on screen. */
  isTermsPageOpen: (id: number) => boolean;
  /** The non-init `Listen` that makes the backend re-send `Terms` for this id. */
  listen: (id: number) => UnknownAction;
}

/**
 * Re-requests `Terms` after an update that changed the CoinGecko project.
 *
 * Such an update drops `coinGeckoLink` from the optimistic merge, and only a `Terms` frame
 * carries the link the backend derived for the new project. Nothing guarantees one follows
 * the ack, so ask for it — otherwise the link button stays hidden until the page is reloaded.
 *
 * Only while that entity's terms page is still open: `Listen` re-points the socket's
 * subscription, and any other section gets its own `Terms` the next time terms is opened.
 *
 * @returns the unsubscribe function of the registered listener.
 */
export const startCoinGeckoLinkRefreshListening = ({
  updateCommand,
  selectTerms,
  isTermsPageOpen,
  listen,
}: CoinGeckoLinkRefreshConfig) =>
  startAppListening({
    actionCreator: actionOnMessage,
    effect: (action, listenerApi) => {
      const { json, skipProcessing, okCommand } = parseResponse(action.payload, [updateCommand]);
      if (skipProcessing || !json || okCommand !== updateCommand) {
        return;
      }

      // The state the ack arrived on: the terms reducer has already settled the update by now.
      const terms = selectTerms(listenerApi.getOriginalState());
      if (terms.id === undefined || !isCoinGeckoLinkAwaitingTerms(terms)) {
        return;
      }

      if (!isTermsPageOpen(terms.id)) {
        return;
      }

      listenerApi.dispatch(listen(terms.id));
    },
  });
